import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { useRouteMatch, useLocation } from 'react-router-dom';
import { GET_RUNTIME_LOGS, GET_RUNTIME_LOGS_COST_ESTIMATE } from '../../graphql';

import LogsTable from './LogsTable';
import { InfoDisplay } from './components/InfoDisplay';
import { GraphHistory } from './components/GraphHistory';
import { LoadingLogo } from '../../components/Modal';
import { groupApolloEvents } from './models';

const formatBytes = (bytes) => {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(bytes >= 10 * 1024 ** 3 ? 0 : 1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(0)} MB`;
  return `${bytes} B`;
};

// One BigQuery scan gets a whole runtime, then "Load More" reveals more of it from memory.
// A runtime's logs are small, and buildRuntimeLogs scans the same single day whatever the LIMIT is
// (LIMIT/OFFSET do not change BigQuery bytes scanned) — so fetching them all in one query costs the
// same as fetching a page, while every extra page used to be another full-price scan. FETCH_LIMIT
// is a generous ceiling that covers any real runtime; PAGE_SIZE is how many rows each click reveals.
const FETCH_LIMIT = 2000;
const PAGE_SIZE = 100;

// eslint-disable-next-line react-hooks/rules-of-hooks
const urlQuery = () => new URLSearchParams(useLocation().search);

export const RuntimeView = () => {
  const match = useRouteMatch();
  const query = urlQuery();
  const startDate = query.get('t');
  const runtimeID = match.params.id;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const { data, loading } = useQuery(GET_RUNTIME_LOGS, {
    variables: {
      runtimeID,
      offset: 0,
      limit: FETCH_LIMIT,
      startDate: parseInt(startDate, 10),
    },
    notifyOnNetworkStatusChange: true,
  });

  // Dry-run estimate ($0) of the single scan that fetched this runtime. Same variables as the real
  // query so the number matches; "Load More" below only slices this in memory, adding no BigQuery cost.
  const { data: estData, loading: estLoading } = useQuery(GET_RUNTIME_LOGS_COST_ESTIMATE, {
    variables: { runtimeID, startDate: parseInt(startDate, 10) },
  });
  const estimate = estData?.analytics?.backendLogsByRuntimeCostEstimate;

  let processedLogs = [];
  if (data && data.analytics.backendLogsByRuntime) {
    if (data.analytics.backendLogsByRuntime.data.length > 0) {
      processedLogs = data.analytics.backendLogsByRuntime.data.map((log) => JSON.parse(log));
    } else {
      console.log('No logs found for this input');
    }
  }

  // Group first (Apollo request/response pairs must stay together), then reveal a slice — so
  // pagination never splits a pair, and "Load More" is a pure in-memory slice with no new query.
  const groupedLogs = groupApolloEvents(processedLogs);
  const visibleLogs = groupedLogs.slice(0, visibleCount);
  const hasMore = visibleCount < groupedLogs.length;

  const infosData = [];
  if (processedLogs && processedLogs.length > 0) {
    const runtimeInfos = processedLogs.find((log) => log.event_name === 'new_runtime');
    if (runtimeInfos && runtimeInfos.event_params) {
      infosData.push({ title: 'Runtime', params: runtimeInfos.event_params, color: 'primary' });
    }
  }
  return (
    <>
      <LoadingLogo show={loading} />
      <Row>
        <Col sm="8">
          {processedLogs ? (
            <>
              <GraphHistory logs={processedLogs} />
              <LogsTable processedLogs={visibleLogs} />
              {hasMore && (
                <Button onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>Load More</Button>
              )}
              {estimate ? (
                <small className="text-muted d-block mt-2">
                  Opening this runtime cost <strong>1 BigQuery scan</strong> (~{formatBytes(estimate.bytes)} ≈ $
                  {estimate.estimatedUsd.toFixed(2)}). <strong>“Load More” is free</strong> — it only reveals logs
                  already loaded, no new scan. You are billed another scan <strong>only if you reload or reopen</strong>{' '}
                  this runtime.
                </small>
              ) : (
                estLoading && <small className="text-muted d-block mt-2">⏳ Estimating BigQuery cost…</small>
              )}
            </>
          ) : (
            <p>No logs found</p>
          )}
        </Col>
        <InfoDisplay data={infosData} />
      </Row>
    </>
  );
};
